<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { api, type AuthKind, type Protocol, type Server } from "../api";

/** Bez `server` formularz dodaje; z `server` — edytuje ten wpis. */
const props = defineProps<{ server?: Server }>();
const emit = defineEmits<{ saved: [Server]; cancel: [] }>();

const editing = computed(() => !!props.server);

const label = ref(props.server?.label ?? "");
const protocol = ref<Protocol>(props.server?.protocol ?? "ssh");
const host = ref(props.server?.host ?? "");
const port = ref<string>(props.server ? String(props.server.port) : "");
const username = ref(props.server?.username ?? "");
const secure = ref(props.server?.secure ?? false);
const authKind = ref<AuthKind>(props.server?.authKind ?? "password");
const secret = ref("");
const passphrase = ref("");

// Sekretu NIE da się wczytać do formularza — panel nigdy nie odsyła haseł ani
// kluczy do przeglądarki (ServerView ich nie zawiera). Przy edycji puste pole
// znaczy więc „zostaw dotychczasowe", a nie „wyczyść".
const keepsSecret = computed(
  () => editing.value && authKind.value === props.server?.authKind,
);

const busy = ref(false);
const err = ref("");

const isSsh = computed(() => protocol.value === "ssh");
const usingKey = computed(() => authKind.value === "key");

// Klucz prywatny działa tylko po SSH (waliduje to też serwer). Gdy ktoś
// przełączy na FTP z wybranym kluczem, cofamy wybór zamiast pozwolić wysłać
// formularz, który i tak wróci błędem.
watch(protocol, (p) => {
  if (p === "ftp" && authKind.value === "key") authKind.value = "password";
  if (p === "ssh") secure.value = false;
});

const portPlaceholder = computed(() => (isSsh.value ? "22" : "21"));

async function save() {
  err.value = "";
  busy.value = true;
  try {
    const body = {
      label: label.value,
      protocol: protocol.value,
      host: host.value,
      // Puste = niech serwer wybierze domyślny port dla protokołu.
      port: port.value ? Number(port.value) : undefined,
      username: username.value,
      secure: secure.value,
      authKind: authKind.value,
      secret: secret.value || undefined,
      passphrase: passphrase.value || undefined,
    };
    const d = props.server
      ? await api.patch<{ server: Server }>(
          `/api/servers/${encodeURIComponent(props.server.id)}`, body)
      : await api.post<{ server: Server }>("/api/servers", body);
    emit("saved", d.server);
  } catch (e) {
    err.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <form style="margin-top:12px" @submit.prevent="save">
    <fieldset>
      <legend>{{ editing ? "Edytuj serwer" : "Nowy serwer" }}</legend>

      <div class="field">
        <label for="sv-label">Nazwa (dla Ciebie)</label>
        <input id="sv-label" v-model="label" type="text" placeholder="np. sklep-produkcja" required>
      </div>

      <div class="field">
        <label for="sv-proto">Protokół</label>
        <select id="sv-proto" v-model="protocol">
          <option value="ssh">ssh</option>
          <option value="ftp">ftp</option>
        </select>
      </div>

      <div class="row">
        <div class="field grow" style="flex:2">
          <label for="sv-host">Host</label>
          <input id="sv-host" v-model="host" type="text" placeholder="1.2.3.4 albo serwer.pl" required>
        </div>
        <div class="field" style="flex:1">
          <label for="sv-port">Port</label>
          <input id="sv-port" v-model="port" type="number" :placeholder="portPlaceholder">
        </div>
      </div>

      <div class="field">
        <label for="sv-user">Użytkownik</label>
        <input id="sv-user" v-model="username" type="text" autocomplete="off" required>
      </div>

      <div v-if="!isSsh" class="field check">
        <input id="sv-secure" v-model="secure" type="checkbox">
        <label for="sv-secure">FTPS (FTP przez TLS)</label>
      </div>

      <div class="field">
        <label for="sv-auth">Uwierzytelnienie</label>
        <select id="sv-auth" v-model="authKind">
          <option value="password">hasło</option>
          <option v-if="isSsh" value="key">klucz prywatny (tylko SSH)</option>
        </select>
      </div>

      <div v-if="!usingKey" class="field">
        <label for="sv-pass">Hasło do serwera</label>
        <input
          id="sv-pass"
          v-model="secret"
          type="password"
          autocomplete="off"
          :placeholder="keepsSecret ? 'bez zmian' : ''"
        >
        <p v-if="keepsSecret" class="hint">Zostaw puste, żeby nie zmieniać hasła.</p>
      </div>

      <template v-else>
        <div class="field">
          <label for="sv-key">Klucz prywatny (PEM)</label>
          <textarea
            id="sv-key"
            v-model="secret"
            spellcheck="false"
            :placeholder="keepsSecret ? 'bez zmian' : '-----BEGIN OPENSSH PRIVATE KEY-----\n…'"
          />
          <p class="hint">
            <template v-if="keepsSecret">Zostaw puste, żeby nie zmieniać klucza. </template>
            Wklej treść klucza. Zapisujemy go zaszyfrowanego (AES-256-GCM).
          </p>
        </div>
        <div class="field">
          <label for="sv-phrase">Passphrase klucza (jeśli jest)</label>
          <input id="sv-phrase" v-model="passphrase" type="password" autocomplete="off">
        </div>
      </template>

      <p v-if="err" class="err">{{ err }}</p>

      <div class="row">
        <button type="submit" :disabled="busy">{{ busy ? "Zapisuję…" : editing ? "Zapisz zmiany" : "Zapisz serwer" }}</button>
        <button type="button" class="sec" @click="emit('cancel')">Anuluj</button>
      </div>
    </fieldset>
  </form>
</template>
